if (!customElements.get("fourpaw-package-selector")) {
  customElements.define(
    "fourpaw-package-selector",
    class FourPawPackageSelector extends HTMLElement {
      connectedCallback() {
        this.form = document.getElementById(this.dataset.productFormId);
        this.productScope = this.closest(".shop-product-section") || this.parentElement || document;
        this.onKitLinkClick = event => {
          const link = event.target.closest("[data-package-kit-link]");
          if (link && !this.contains(link)) this.trackUpsell("kit_opened", "kit", {source:"kit_upgrade"});
        };
        this.productScope.addEventListener("click", this.onKitLinkClick);
        this.variantRevision = 0;
        this.cards = Array.from(this.querySelectorAll("[data-package-card]"));
        this.extraGroup = this.querySelector("[data-package-extra-group]");
        this.extraCards = Array.from(this.querySelectorAll("[data-package-extra-card]"))
          .sort((a, b) => Number(a.dataset.packageExtraSlot || 0) - Number(b.dataset.packageExtraSlot || 0));
        this.mainVariants = this.readJson(
          "[data-fourpaw-main-variants]",
          "[data-fourpaw-product-variants]",
        );
        this.mainOptions = this.readJson("[data-fourpaw-main-options]");
        this.bungeeVariants = this.readJson("[data-fourpaw-bungee-variants]");
        this.bungeeOptions = this.readJson("[data-fourpaw-bungee-options]");
        this.bungeeVariantId = 0;
        this.bungeeOverrideMainColor = "";
        this.collarVariants = this.readJson("[data-fourpaw-collar-variants]");
        this.collarOptions = this.readJson("[data-fourpaw-collar-options]");
        this.collarColorOverride = "";
        this.collarOverrideMainColor = "";
        this.safebeltVariants = this.readJson("[data-fourpaw-safebelt-variants]");
        this.safebeltMode = this.dataset.safebeltMode === "true";
        this.safebeltVariantId = Number(this.querySelector("[data-package-safebelt-color]")?.value || 0);
        this.upsellExposureTimers = new Map();
        this.upsellExposed = new Set();
        this.upsellStages = {};
        this.upsellJourney = this.newGroup();
        this.kitVariants = this.readJson("[data-fourpaw-kit-variants]");
        this.colorOptionLabel = this.dataset.colorOptionName || "Color";
        this.sizeOptionLabel = this.dataset.sizeOptionName || "Size";
        this.colorOptionName = this.normalize(this.colorOptionLabel);
        this.sizeOptionName = this.normalize(this.sizeOptionLabel);
        this.giftMode = this.dataset.giftMode || "legacy_pack";
        this.giftVariantId = Number(this.dataset.giftVariantId || 0);
        this.giftTagsVariantId = Number(this.dataset.giftTagsVariantId || 0);
        this.giftDispenserVariantId = Number(this.dataset.giftDispenserVariantId || 0);
        this.giftRollVariantId = Number(this.dataset.giftRollVariantId || 0);
        this.giftAvailable = this.dataset.giftAvailable === "true";
        this.copyPersonalizationToGiftPack = this.dataset.copyPersonalizationToGiftPack === "true";
        this.giftPersonalizationLabel = this.dataset.giftPersonalizationLabel || "Gift personalization";
        this.giftPersonalizationEmpty = this.dataset.giftPersonalizationEmpty || "Not requested";
        this.harnessTitle = this.dataset.harnessTitle || "SafeWalk Harness";
        this.bungeeTitle = this.dataset.bungeeTitle || "Bungee Leash";
        this.collarTitle = this.dataset.collarTitle || "Padded Collar";
        this.addonDiscountPercentage = Math.min(
          100,
          Math.max(0, Number(this.dataset.addonDiscountPercentage || 0)),
        );
        this.offerHeading = this.querySelector("[data-package-offer-heading]");
        this.offerHeadingTemplate = this.dataset.offerHeadingTemplate || "";
        this.kachingBridgeEnabled = this.dataset.kachingBridgeEnabled === "true";
        this.kachingMainProductId = String(this.dataset.mainProductId || "");
        this.kachingMainProductHandle = this.dataset.mainProductHandle || "";
        this.kachingHandles = {
          bungee: this.dataset.bungeeHandle || "bungee-leash",
          collar: this.dataset.collarHandle || "padded-collar",
        };
        this.kachingBridgeActive = false;
        this.kachingExtraRowsAvailable = false;
        this.kachingExtraBridgeActive = false;
        this.discountLoadingLabel = this.dataset.discountLoadingLabel || "Loading Add-On Discount…";
        this.selectedPackage = this.packageName();
        this.querySelectorAll("[data-package-product-image]").forEach((image) => {
          image.dataset.packageFallbackSrc = image.currentSrc || image.src || "";
        });
        this.sticky = this.querySelector("[data-package-sticky]");
        this.stickyButton = this.querySelector("[data-package-sticky-button]");
        this.infoDialog = this.querySelector("[data-package-info-dialog]");
        this.infoPanel = this.querySelector("[data-package-info-panel]");
        this.infoCloseButton = this.querySelector("[data-package-info-close]");
        this.infoImageCache = new Map();
        if (this.sticky) {
          const componentStyles = window.getComputedStyle(this);
          [
            "--fourpaw-package-navy",
            "--fourpaw-package-gold",
            "--fourpaw-package-ink",
            "--fourpaw-package-muted",
            "--fourpaw-package-border",
            "--fourpaw-package-surface",
          ].forEach((property) => {
            this.sticky.style.setProperty(property, componentStyles.getPropertyValue(property));
          });
          document.body.append(this.sticky);
        }
        this.packageGroup = this.newGroup();
        this.prepareSubmit = (event) => {
          this.releaseRootFields();
          this.reconcileMainVariant();
          this.packageGroup = this.newGroup();
          let selection = this.currentSelection();
          if (!selection.main || !selection.available) return;
          this.syncKaching(selection);
          this.reconcileMainVariant();
          selection = this.currentSelection();
          if (!this.purchaseAvailable(selection)) {
            event.preventDefault();
            event.stopImmediatePropagation();
            this.syncButton(false, this.discountLoadingLabel, selection);
            return;
          }
          this.pinMainVariant(selection.main);
          this.lastSubmittedUpsellSelection = selection;
          const hasKachingUpsells = Boolean(
            selection.includesBungee ||
            selection.includesCollar ||
            selection.extras?.some((extra) => extra.selected),
          );
          if (!hasKachingUpsells) this.renewKachingContractId();
          this.syncFormLines(selection);
          this.suspendRootFields();
          window.setTimeout(() => this.releaseRootFields(), 0);
        };
        this.finalizeFormData = (event) => {
          this.finalizeKachingFormData(event.detail?.formData);
          this.attachUpsellEvidence(event.detail?.formData);
        };

        this.onAddonChange = (event) => {
          if (!event.target.matches("[data-package-input]")) return;
          const addon = event.target.closest("[data-package-addon]")?.dataset.packageAddon;
          if (["safebelt","bungee","collar"].includes(addon)) this.trackUpsell(event.target.checked ? "selected" : "deselected", addon, {source:"card"});
          this.selectedPackage = this.packageName();
          this.dispatchEvent(
            new CustomEvent("fourpaw:package-change", {
              bubbles: true,
              detail: {
                package: this.selectedPackage,
                includesBungee: this.addonChecked("bungee"),
                includesCollar: this.addonChecked("collar"),
                includesSafeBelt: this.addonChecked("safebelt"),
              },
            }),
          );
          this.update();
        };
        this.addEventListener("change", this.onAddonChange);

        this.onExtraChange = (event) => {
          const input = event.target.closest?.("[data-package-extra-input]");
          const option = event.target.closest?.("[data-package-extra-option]");
          if (!input && !option) return;

          if (input) {
            const slot = Number(input.closest("[data-package-extra-card]")?.dataset.packageExtraSlot || 0);
            if (input.checked) {
              this.extraCards.slice(0, Math.max(0, slot - 1)).forEach((card) => {
                const previous = card.querySelector("[data-package-extra-input]");
                if (previous && !previous.disabled) previous.checked = true;
              });
            } else {
              this.extraCards.slice(Math.max(0, slot - 1)).forEach((card) => {
                const current = card.querySelector("[data-package-extra-input]");
                if (current) current.checked = false;
              });
            }
          }

          this.normalizeExtraSequence();
          this.updateExtraCards();
          this.update();
        };
        this.addEventListener("change", this.onExtraChange);

        this.onInfoClick = (event) => {
          const add = event.target.closest("[data-package-info-add]");
          if (add && this.contains(add)) {
            const addon = add.dataset.packageInfoAdd;
            const input = this.addonInput(addon);
            if (!input || input.disabled) return;
            event.preventDefault();
            const wasSelected = input.checked;
            input.checked = true;
            if (!wasSelected) this.trackUpsell("selected", addon, {source:"popup"});
            this.selectedPackage = this.packageName();
            this.dispatchEvent(new CustomEvent("fourpaw:package-change", {bubbles:true,detail:{package:this.selectedPackage,includesBungee:this.addonChecked("bungee"),includesSafeBelt:this.addonChecked("safebelt"),includesCollar:this.addonChecked("collar")}}));
            this.update();
            this.infoDialog?.close();
            return;
          }
          const kit = event.target.closest("[data-package-kit-link]");
          if (kit && this.contains(kit)) { this.trackUpsell("kit_opened", "kit", {source:"kit_upgrade"}); return; }
          const button = event.target.closest("[data-package-info-button], [data-package-info-open]");
          if (!button || !this.contains(button)) {
            const card = event.target.closest("[data-package-card]");
            if (card && this.contains(card) && !event.target.closest("label, input, button, select, option, a")) {
              const input = card.querySelector("[data-package-input]");
              if (input && !input.disabled) input.click();
            }
            return;
          }
          event.preventDefault();
          event.stopPropagation();
          const addon = button.dataset.packageInfoButton || button.dataset.packageInfoOpen;
          const source = event.target.closest(".fourpaw-package__media") ? "image" : event.target.closest(".fourpaw-package__title-line") ? "title" : "details";
          this.openInfo(addon, true, button, source);
        };
        this.onInfoClose = () => this.infoDialog?.close();
        this.onInfoBackdropClick = (event) => {
          if (!this.infoDialog?.open || this.infoPanel?.contains(event.target)) return;
          this.infoDialog.close();
        };
        this.onInfoDialogClose = () => {
          const addon = this.infoDialog?.dataset.packageAddon;
          if (["safebelt","bungee","collar"].includes(addon)) this.trackUpsell("details_closed", addon, {seconds:Math.min(300,Math.floor((Date.now() - (this.infoOpenedAt || Date.now())) / 1000))});
          this.popupEndObserver?.disconnect();
          window.clearTimeout(this.popupEndTimer);
          const opener = this.infoReturnFocus;
          this.infoReturnFocus = null;
          window.requestAnimationFrame(() => {
            if (opener?.isConnected) opener.focus({ preventScroll: true });
          });
        };
        this.addEventListener("click", this.onInfoClick);
        this.onInfoIntent = (event) => {
          const button = event.target.closest?.("[data-package-info-button], [data-package-info-open]");
          if (button && this.contains(button)) this.warmInfoImage(button.dataset.packageInfoButton || button.dataset.packageInfoOpen);
        };
        this.addEventListener("pointerover", this.onInfoIntent);
        this.addEventListener("focusin", this.onInfoIntent);
        this.infoCloseButton?.addEventListener("click", this.onInfoClose);
        this.infoDialog?.addEventListener("click", this.onInfoBackdropClick);
        this.infoDialog?.addEventListener("close", this.onInfoDialogClose);
        this.onSafeBeltColor = (event) => {
          if (!event.target.matches("[data-package-safebelt-color]")) return;
          const variant = this.safebeltVariants.find(v => String(v.id) === event.target.value && v.available);
          if (!variant || String(variant.id) === String(this.safebeltVariantId)) return;
          this.safebeltVariantId = Number(variant.id);
          this.trackUpsell("color_selected", "safebelt", {source:event.target.closest("dialog") ? "popup" : "card"});
          this.update();
        };
        this.addEventListener("change", this.onSafeBeltColor);
        this.onBungeeColor = event => {
          if (!event.target.matches("[data-package-bungee-color]")) return;
          const variant = this.bungeeVariants.find(v => String(v.id) === event.target.value && v.available);
          if (!variant) return;
          this.bungeeOverrideMainColor = this.normalize(this.currentSelection().color);
          this.bungeeVariantId = Number(variant.id);
          this.trackUpsell("color_selected", "bungee", {color:variant.options?.[0],source:event.target.closest("dialog") ? "popup" : "card"});
          this.update();
        };
        this.addEventListener("change", this.onBungeeColor);
        this.onCollarColor = event => {
          if (!event.target.matches("[data-package-collar-color]")) return;
          const selection = this.currentSelection();
          const variant = this.matchingVariant(this.collarVariants, this.collarOptions, event.target.value, selection.size, true);
          if (!variant) return;
          this.collarOverrideMainColor = this.normalize(selection.color);
          this.collarColorOverride = event.target.value;
          this.trackUpsell("color_selected", "collar", {color:variant.options?.[this.optionIndex(this.collarOptions, this.colorOptionName, 0)],source:event.target.closest("dialog") ? "popup" : "card"});
          this.update();
        };
        this.addEventListener("change", this.onCollarColor);
        this.offerFitObserver = window.ResizeObserver ? new ResizeObserver(() => this.fitOfferLines()) : null;
        const offer = this.querySelector(".fourpaw-package__offer");
        if (offer) this.offerFitObserver?.observe(offer);
        document.fonts?.ready.then(() => { if (this.isConnected) this.fitOfferLines(); });
        this.onFeatureToggle = (event) => {
          if (event.target.matches?.("[data-package-feature]") && event.target.open && this.infoDialog?.open) this.trackUpsell("feature_opened", "safebelt", {feature:event.target.dataset.packageFeature});
        };
        this.infoDialog?.addEventListener("toggle", this.onFeatureToggle, true);

        this.onVariantChange = (event) => {
          const eventFormId = String(event.detail?.productFormId || "");
          if (eventFormId && eventFormId !== String(this.dataset.productFormId || "")) return;
          const eventVariant = event.detail?.variant;
          if (
            event.type === "simple-variant-picker:variantChange" &&
            eventVariant &&
            this.mainVariants.some((variant) => String(variant.id) === String(eventVariant.id))
          ) {
            this.pinMainVariant(eventVariant);
          }
          const revision = ++this.variantRevision;
          window.requestAnimationFrame(() => {
            if (revision !== this.variantRevision) return;
            this.reconcileMainVariant();
            this.update();
          });
        };
        document.addEventListener("simple-variant-picker:variantChange", this.onVariantChange);
        document.addEventListener("simple-variant-picker:optionChange", this.onVariantChange);
        document.addEventListener("variant:change", this.onVariantChange);
        document.addEventListener("variant:imageChanged", this.onVariantChange);
        this.onAddToCartSucceeded = (event) => {
          if (!event.target?.contains?.(this.form)) return;
          const selection = this.lastSubmittedUpsellSelection;
          if (selection?.includesSafeBelt) this.trackUpsell("cart_added", "safebelt", {color:selection.safebelt?.options?.[0]});
          if (selection?.includesBungee) this.trackUpsell("cart_added", "bungee", {color:selection.bungee?.options?.[0] || selection.color});
          if (selection?.includesCollar) this.trackUpsell("cart_added", "collar", {color:selection.collar?.options?.[this.optionIndex(this.collarOptions, this.colorOptionName, 0)] || selection.color});
          this.lastSubmittedUpsellSelection = null;
          this.clearGiftPersonalization();
        };
        document.addEventListener("fourpaw:product-form:add-to-cart-succeeded", this.onAddToCartSucceeded);
        this.bindForm();
        this.startKachingBridge();

        this.onStickyClick = (event) => {
          event.preventDefault();
          event.stopPropagation();
          const purchaseButton = this.purchaseButton || this.form?.querySelector('[type="submit"]');
          if (!purchaseButton || purchaseButton.disabled) return;
          purchaseButton.click();
        };
        this.stickyButton?.addEventListener("click", this.onStickyClick);

        window.setTimeout(() => {
          this.update();
          this.observePurchaseButton();
          this.observeInfoMedia();
          this.observeUpsellExposure();
        }, 0);
      }

      disconnectedCallback() {
        document.removeEventListener("simple-variant-picker:variantChange", this.onVariantChange);
        document.removeEventListener("simple-variant-picker:optionChange", this.onVariantChange);
        document.removeEventListener("variant:change", this.onVariantChange);
        document.removeEventListener("variant:imageChanged", this.onVariantChange);
        document.removeEventListener("fourpaw:product-form:add-to-cart-succeeded", this.onAddToCartSucceeded);
        this.removeEventListener("change", this.onAddonChange);
        this.productScope?.removeEventListener("click", this.onKitLinkClick);
        this.removeEventListener("change", this.onSafeBeltColor);
        this.removeEventListener("change", this.onBungeeColor);
        this.removeEventListener("change", this.onCollarColor);
        this.offerFitObserver?.disconnect();
        this.infoDialog?.removeEventListener("toggle", this.onFeatureToggle, true);
        this.upsellObserver?.disconnect();
        this.popupEndObserver?.disconnect();
        this.upsellExposureTimers?.forEach(timer => window.clearTimeout(timer));
        window.clearTimeout(this.popupEndTimer);
        this.removeEventListener("change", this.onExtraChange);
        this.removeEventListener("click", this.onInfoClick);
        this.removeEventListener("pointerover", this.onInfoIntent);
        this.removeEventListener("focusin", this.onInfoIntent);
        this.infoMediaObserver?.disconnect();
        window.clearTimeout(this.infoWarmTimer);
        this.infoCloseButton?.removeEventListener("click", this.onInfoClose);
        this.infoDialog?.removeEventListener("click", this.onInfoBackdropClick);
        this.infoDialog?.removeEventListener("close", this.onInfoDialogClose);
        this.unbindForm();
        this.stickyButton?.removeEventListener("click", this.onStickyClick);
        this.purchaseObserver?.disconnect();
        this.kachingObserver?.disconnect();
        if (this.kachingSyncFrame) window.cancelAnimationFrame(this.kachingSyncFrame);
        if (this.onStickyPositionChange) {
          window.removeEventListener("scroll", this.onStickyPositionChange);
          window.removeEventListener("resize", this.onStickyPositionChange);
        }
        if (this.onStickyVisibilityChange) {
          document.removeEventListener("visibilitychange", this.onStickyVisibilityChange);
        }
        if (this.stickyPositionFrame) window.cancelAnimationFrame(this.stickyPositionFrame);
        this.releaseRootFields();
        this.sticky?.remove();
      }

      bindForm() {
        const nextForm = document.getElementById(this.dataset.productFormId);
        if (nextForm === this.boundForm) {
          this.form = nextForm;
          return;
        }
        this.unbindForm();
        this.form = nextForm;
        this.boundForm = nextForm;
        if (!this.boundForm) return;
        this.boundForm.addEventListener("submit", this.prepareSubmit, true);
        this.boundForm.addEventListener("fourpaw:product-form-data", this.finalizeFormData);
        this.boundVariantInput = this.boundForm.querySelector('[name="id"]');
        this.boundVariantInput?.addEventListener("change", this.onVariantChange);
      }

      unbindForm() {
        this.boundForm?.removeEventListener("submit", this.prepareSubmit, true);
        this.boundForm?.removeEventListener("fourpaw:product-form-data", this.finalizeFormData);
        this.boundVariantInput?.removeEventListener("change", this.onVariantChange);
        this.boundForm = null;
        this.boundVariantInput = null;
      }

      newGroup() {
        const randomPart = window.crypto?.getRandomValues
          ? Array.from(window.crypto.getRandomValues(new Uint8Array(4)), (byte) => byte.toString(36)).join("")
          : Math.random().toString(36).slice(2, 8);
        return `${Date.now().toString(36)}${randomPart}`;
      }

      readJson(selector, sharedSelector = "") {
        const node =
          this.querySelector(selector) ||
          (sharedSelector
            ? this.closest(".shop-product-section")?.querySelector(sharedSelector)
            : null);
        if (!node) return [];
        try {
          const parsed = JSON.parse(node.textContent || "[]");
          return Array.isArray(parsed) ? parsed : [];
        } catch (error) {
          console.warn("FourPaw package data could not be read.", error);
          return [];
        }
      }

      normalize(value) {
        return String(value || "").trim().toLowerCase();
      }

      variantPicker() {
        const formId = String(this.dataset.productFormId || "");
        return Array.from(this.productScope?.querySelectorAll?.(".simple-variant-picker") || []).find(
          (picker) => !picker.dataset.productFormId || picker.dataset.productFormId === formId,
        ) || null;
      }

      selectedMainVariantFromPicker() {
        const picker = this.variantPicker();
        if (!picker || this.mainOptions.length === 0) return null;
        const selected = new Map();
        picker.querySelectorAll(".simple-variant-picker__radio:checked").forEach((field) => {
          const position = Number(field.dataset.optionPosition || 0) - 1;
          if (position >= 0) selected.set(position, field.value);
        });
        picker.querySelectorAll(".simple-variant-picker__select").forEach((field) => {
          const position = Number(field.dataset.optionPosition || 0) - 1;
          if (position >= 0 && field.value) selected.set(position, field.value);
        });
        if (selected.size < this.mainOptions.length) return null;
        return this.mainVariants.find((variant) =>
          this.mainOptions.every((_, index) =>
            this.normalize(variant.options?.[index]) === this.normalize(selected.get(index)),
          ),
        ) || null;
      }

      pinMainVariant(variant) {
        if (!variant?.id) return null;
        this.bindForm();
        const variantId = String(variant.id);
        const input = this.boundVariantInput || this.form?.querySelector('[name="id"]');
        if (input) {
          input.value = variantId;
          input.setAttribute("value", variantId);
        }
        const picker = this.variantPicker();
        if (picker) picker.dataset.selectedVariantId = variantId;
        this.desiredMainVariantId = variantId;
        return variant;
      }

      reconcileMainVariant() {
        const selectedVariant = this.selectedMainVariantFromPicker();
        if (selectedVariant) return this.pinMainVariant(selectedVariant);
        const desiredVariant = this.mainVariants.find(
          (variant) => String(variant.id) === String(this.desiredMainVariantId || ""),
        );
        return desiredVariant ? this.pinMainVariant(desiredVariant) : null;
      }

      clearGiftPersonalization() {
        const scope = this.productScope || this.form || document;
        scope.querySelectorAll("[data-fourpaw-personalization-role]").forEach((wrapper) => {
          const role = wrapper.dataset.fourpawPersonalizationRole || "";
          if (!['dog_name', 'phone'].includes(role)) return;
          const field = wrapper.matches("input, textarea") ? wrapper : wrapper.querySelector("input, textarea");
          if (!field || field.value === "") return;
          field.value = "";
          field.dispatchEvent(new Event("input", { bubbles: true }));
          field.dispatchEvent(new Event("change", { bubbles: true }));
        });
      }

      setText(node, value) {
        const next = String(value ?? "");
        if (node && node.textContent !== next) node.textContent = next;
      }

      startKachingBridge() {
        if (!this.kachingBridgeEnabled) return;
        this.kachingObserver = new MutationObserver((mutations) => {
          if (mutations.some((mutation) => this.mutationTouchesKaching(mutation))) {
            this.scheduleKachingSync();
          }
        });
        // Kaching is rendered inside this product section. Observing the whole
        // document made unrelated app, analytics, and cart mutations wake the
        // offer bridge for the lifetime of the PDP.
        this.kachingObserver.observe(this.productScope || document.documentElement, {
          childList: true,
          subtree: true,
        });
        this.scheduleKachingSync();
      }

      mutationTouchesKaching(mutation) {
        const touchesBundle = (node) => {
          const element = node?.nodeType === Node.ELEMENT_NODE ? node : node?.parentElement;
          return Boolean(
            element?.matches?.("kaching-bundle") ||
            element?.closest?.("kaching-bundle") ||
            element?.querySelector?.("kaching-bundle"),
          );
        };
        return touchesBundle(mutation.target) ||
          Array.from(mutation.addedNodes || []).some(touchesBundle) ||
          Array.from(mutation.removedNodes || []).some(touchesBundle);
      }

      scheduleKachingSync() {
        if (!this.kachingBridgeEnabled || this.kachingSyncFrame) return;
        this.kachingSyncFrame = window.requestAnimationFrame(() => {
          this.kachingSyncFrame = null;
          this.reconcileMainVariant();
          let selection = this.currentSelection();
          if (!selection.main) return;
          this.syncKaching(selection);
          this.reconcileMainVariant();
          this.updateExtraCards();
          selection = this.currentSelection();
          this.syncFormLines(selection);
          const available = this.purchaseAvailable(selection);
          this.syncSticky(selection, available);
          this.syncButton(available, available ? "" : this.discountLoadingLabel, selection);
        });
      }

      kachingBundle() {
        if (!this.kachingBridgeEnabled || !this.kachingMainProductId) return null;
        return document.querySelector(`kaching-bundle[product-id="${this.kachingMainProductId}"]`);
      }

      kachingUpsells(bundle, handle) {
        if (!bundle || !handle) return null;
        return Array.from(bundle.querySelectorAll("[data-upsell-id]")).filter((row) =>
          Array.from(row.querySelectorAll('a[href*="/products/"]')).some((anchor) => {
            try {
              const path = new URL(anchor.href, window.location.origin).pathname.replace(/\/$/, "");
              return path.endsWith(`/products/${handle}`);
            } catch {
              return false;
            }
          }),
        );
      }

      kachingUpsell(bundle, addon) {
        return this.kachingUpsells(bundle, this.kachingHandles[addon])?.[0] || null;
      }

      kachingSlotMap(bundle = this.kachingBundle()) {
        if (!bundle) return null;
        const map = {
          b: (this.kachingUpsells(bundle, this.kachingHandles.bungee) || [])
            .map((row) => this.kachingRowId(row))
            .filter(Boolean)
            .slice(0, 4),
          c: (this.kachingUpsells(bundle, this.kachingHandles.collar) || [])
            .map((row) => this.kachingRowId(row))
            .filter(Boolean)
            .slice(0, 4),
          h: (this.kachingUpsells(bundle, this.kachingMainProductHandle) || [])
            .map((row) => this.kachingRowId(row))
            .filter(Boolean)
            .slice(0, 3),
        };
        return map.b.length === 4 && map.c.length === 4 && map.h.length === 3 ? map : null;
      }

      kachingRowId(row) {
        return String(row?.getAttribute("data-upsell-id") || "").trim();
      }

      kachingRowById(bundle, id) {
        if (!bundle || !id) return null;
        return Array.from(bundle.querySelectorAll("[data-upsell-id]")).find(
          (row) => this.kachingRowId(row) === String(id),
        ) || null;
      }

      clearKachingExtraRows(rows = []) {
        rows.forEach((row) => {
          const toggle = row.querySelector(".kaching-bundles__upsell__checkbox");
          if (toggle?.classList.contains("kaching-bundles__upsell__checkbox--selected")) toggle.click();
        });
        this.extraCards.forEach((card) => {
          delete card.dataset.kachingUpsellId;
          const input = card.querySelector("[data-package-extra-input]");
          if (input) {
            input.checked = false;
            input.disabled = true;
          }
          card.querySelectorAll("[data-package-extra-option]").forEach((field) => {
            field.disabled = true;
          });
          card.classList.add("is-disabled", "is-locked");
          card.setAttribute("aria-disabled", "true");
        });
        if (this.extraGroup) this.extraGroup.hidden = true;
        this.kachingExtraRowsAvailable = false;
        this.kachingExtraBridgeActive = false;
      }

      mappedKachingExtraRows(bundle) {
        if (!bundle || !this.kachingMainProductHandle || this.extraCards.length !== 3) return [];
        const candidates = this.kachingUpsells(bundle, this.kachingMainProductHandle) || [];
        if (candidates.length !== 3) {
          this.clearKachingExtraRows(candidates);
          return [];
        }

        const candidateIds = candidates.map((row) => this.kachingRowId(row));
        if (candidateIds.some((id) => !id) || new Set(candidateIds).size !== 3) {
          this.clearKachingExtraRows(candidates);
          return [];
        }

        const capturedIds = this.extraCards.map((card) => card.dataset.kachingUpsellId || "");
        if (capturedIds.every(Boolean) && new Set(capturedIds).size === 3) {
          const capturedRows = capturedIds.map((id) => this.kachingRowById(bundle, id));
          if (capturedRows.every(Boolean)) return capturedRows;
        }

        this.extraCards.forEach((card, index) => {
          card.dataset.kachingUpsellId = candidateIds[index];
        });
        return candidateIds.map((id) => this.kachingRowById(bundle, id)).filter(Boolean);
      }

      kachingExtraLabel(row, card) {
        const fallbackTitle = card.dataset.packageExtraFallbackTitle || "Extra harness";
        const fallbackDiscount = card.dataset.packageExtraFallbackDiscount || "";
        return { title: fallbackTitle, discount: fallbackDiscount };
      }

      syncKachingExtraPresentation(row, card) {
        if (!row || !card) return;
        const label = this.kachingExtraLabel(row, card);
        const title = card.querySelector("[data-package-extra-title]");
        const discount = card.querySelector("[data-package-extra-discount-label]");
        const price = card.querySelector("[data-package-extra-price]");
        const compare = card.querySelector("[data-package-extra-compare]");
        const image = card.querySelector("[data-package-extra-image]");
        const remoteImage = row.querySelector(".kaching-bundles__upsell__image");
        this.setText(title, label.title);
        this.setText(discount, label.discount);
        if (price && row.querySelector(".kaching-bundles__upsell__price")) {
          this.setText(price, row.querySelector(".kaching-bundles__upsell__price").textContent.trim());
        }
        if (compare && row.querySelector(".kaching-bundles__upsell__full-price")) {
          this.setText(compare, row.querySelector(".kaching-bundles__upsell__full-price").textContent.trim());
        }
        if (image && remoteImage?.src) image.src = remoteImage.src;
      }
      setKachingOption(row, optionName, optionValue) {
        if (!row || !optionValue) return;
        const select = Array.from(row.querySelectorAll("select")).find(
          (field) => this.normalize(field.getAttribute("aria-label")) === this.normalize(optionName),
        );
        if (!select) return;
        const option = Array.from(select.options).find(
          (candidate) => this.normalize(candidate.value) === this.normalize(optionValue),
        );
        if (!option || select.value === option.value) return;
        select.value = option.value;
        select.dispatchEvent(new Event("change", { bubbles: true }));
      }

      syncKachingAddon(bundle, addon, selected, color, size) {
        let row = this.kachingUpsell(bundle, addon);
        if (!row) return false;
        if (selected) {
          this.setKachingOption(row, this.colorOptionLabel, color);
          if (addon === "collar") this.setKachingOption(row, this.sizeOptionLabel, size);
          row = this.kachingUpsell(bundle, addon) || row;
        }
        const toggle = row.querySelector(".kaching-bundles__upsell__checkbox");
        if (!toggle) return false;
        const isSelected = toggle.classList.contains("kaching-bundles__upsell__checkbox--selected");
        if (isSelected !== selected) toggle.click();
        return true;
      }

      syncKachingExtraRow(bundle, row, card, extra) {
        const id = card?.dataset.kachingUpsellId;
        if (!bundle || !row || !card || !extra || !id) return false;

        card.querySelectorAll("[data-package-extra-option]").forEach((field) => {
          this.setKachingOption(row, field.dataset.packageExtraOptionName || field.getAttribute("aria-label"), field.value);
          row = this.kachingRowById(bundle, id) || row;
        });

        const remoteFieldsMatch = Array.from(card.querySelectorAll("[data-package-extra-option]")).every((field) => {
          const name = field.dataset.packageExtraOptionName || field.getAttribute("aria-label");
          const remote = Array.from(row.querySelectorAll("select")).find(
            (select) => this.normalize(select.getAttribute("aria-label")) === this.normalize(name),
          );
          return remote && this.normalize(remote.value) === this.normalize(field.value);
        });
        const toggle = row.querySelector(".kaching-bundles__upsell__checkbox");
        if (!toggle || !remoteFieldsMatch) return false;
        const isSelected = toggle.classList.contains("kaching-bundles__upsell__checkbox--selected");
        if (isSelected !== extra.selected) toggle.click();
        this.syncKachingExtraPresentation(this.kachingRowById(bundle, id) || row, card);
        return true;
      }

      syncKachingExtras(bundle, extras) {
        const rows = this.mappedKachingExtraRows(bundle);
        if (rows.length !== 3) return false;
        this.kachingExtraRowsAvailable = true;
        if (this.extraGroup) this.extraGroup.hidden = false;
        this.normalizeExtraSequence();
        this.updateExtraCards();

        const currentExtras = this.extraSelections();
        const ready = rows.every((row, index) =>
          this.syncKachingExtraRow(bundle, row, this.extraCards[index], currentExtras[index] || extras?.[index]),
        );
        this.kachingExtraBridgeActive = ready;
        return ready;
      }

      syncKaching(selection) {
        if (!this.kachingBridgeEnabled) {
          this.kachingBridgeActive = false;
          return false;
        }
        const bundle = this.kachingBundle();
        const contract = this.form?.querySelector('input[name="properties[__kaching_bundles]"]');
        if (!bundle || !contract) {
          this.kachingBridgeActive = false;
          this.clearKachingExtraRows();
          return false;
        }
        bundle.classList.add("fourpaw-kaching-bridge");
        const bungeeReady = this.kachingUpsell(bundle, "bungee")
          ? this.syncKachingAddon(bundle, "bungee", selection.includesBungee, selection.bungee?.options?.[0] || selection.color, selection.size)
          : this.bungeeVariants.length === 0;
        const collarReady = this.kachingUpsell(bundle, "collar")
          ? this.syncKachingAddon(bundle, "collar", selection.includesCollar, selection.collar?.options?.[this.optionIndex(this.collarOptions, this.colorOptionName, 0)] || selection.color, selection.size)
          : this.collarVariants.length === 0;
        this.syncKachingExtras(bundle, selection.extras);
        this.kachingBridgeActive = Boolean(bungeeReady && collarReady);
        return this.kachingBridgeActive;
      }

      purchaseAvailable(selection) {
        if (!selection?.available) return false;
        const hasAddon = selection.includesBungee || selection.includesCollar;
        const hasExtra = selection.extras?.some((extra) => extra.selected);
        const addonReady = !this.kachingBridgeEnabled || !hasAddon || this.kachingBridgeActive;
        const extrasReady = !hasExtra || (this.kachingExtraRowsAvailable && this.kachingExtraBridgeActive);
        return addonReady && extrasReady;
      }

      optionIndex(options, name, fallback) {
        const index = options.findIndex((option) => this.normalize(option) === name);
        return index >= 0 ? index : fallback;
      }

      currentMainVariant() {
        const selectedVariant = this.selectedMainVariantFromPicker();
        if (selectedVariant) return this.pinMainVariant(selectedVariant);
        const desiredVariant = this.mainVariants.find(
          (variant) => String(variant.id) === String(this.desiredMainVariantId || ""),
        );
        if (desiredVariant) return this.pinMainVariant(desiredVariant);
        const id = this.form?.querySelector('[name="id"]')?.value;
        return this.mainVariants.find((variant) => String(variant.id) === String(id)) || this.mainVariants[0] || null;
      }

      matchingVariant(variants, options, color, size, requiresSize) {
        const colorIndex = this.optionIndex(options, this.colorOptionName, 0);
        const sizeIndex = this.optionIndex(options, this.sizeOptionName, requiresSize ? 1 : -1);
        return (
          variants.find((variant) => {
            const colorMatches = this.normalize(variant.options?.[colorIndex]) === this.normalize(color);
            const sizeMatches =
              !requiresSize || this.normalize(variant.options?.[sizeIndex]) === this.normalize(size);
            return colorMatches && sizeMatches && variant.available;
          }) || null
        );
      }

      extraDiscountPrice(variant, discountPercentage) {
        const standalone = this.standalonePrice(variant);
        const percentage = Math.min(100, Math.max(0, Number(discountPercentage || 0)));
        return standalone - Math.floor((standalone * percentage) / 100);
      }

      extraSelections() {
        return this.extraCards.map((card) => {
          const input = card.querySelector("[data-package-extra-input]");
          const fields = Array.from(card.querySelectorAll("[data-package-extra-option]"));
          const colorField = fields.find(
            (field) => this.normalize(field.dataset.packageExtraOptionName) === this.colorOptionName,
          );
          const sizeField = fields.find(
            (field) => this.normalize(field.dataset.packageExtraOptionName) === this.sizeOptionName,
          );
          const color = colorField?.value || "";
          const size = sizeField?.value || "";
          const variant = this.matchingVariant(this.mainVariants, this.mainOptions, color, size, true);
          const selected = Boolean(input?.checked && !input.disabled && variant?.available);
          const discount = Number(card.dataset.packageExtraDiscount || 0);
          return {
            card,
            input,
            variant,
            color,
            size,
            discount,
            selected,
            price: this.extraDiscountPrice(variant, discount),
          };
        });
      }

      normalizeExtraSequence() {
        if (!this.kachingExtraRowsAvailable) return;
        let previousSelected = true;
        this.extraCards.forEach((card, index) => {
          const input = card.querySelector("[data-package-extra-input]");
          const locked = index > 0 && !previousSelected;
          const unavailable = card.classList.contains("is-unavailable");
          if (locked && input) input.checked = false;
          card.classList.toggle("is-locked", locked);
          card.classList.toggle("is-disabled", locked);
          card.setAttribute("aria-disabled", locked ? "true" : "false");
          if (input) input.disabled = locked || unavailable;
          card.querySelectorAll("[data-package-extra-option]").forEach((field) => {
            field.disabled = locked;
          });
          previousSelected = Boolean(input?.checked && !locked && !unavailable);
        });
      }

      updateExtraCards() {
        if (!this.kachingExtraRowsAvailable) return;
        this.normalizeExtraSequence();
        const extras = this.extraSelections();
        extras.forEach((extra) => {
          const { card, input, variant, selected, discount, color, size } = extra;
          const unavailable = !variant?.available;
          if (unavailable && input?.checked) input.checked = false;
          card.classList.toggle("is-unavailable", unavailable);
          card.classList.toggle("is-selected", selected && !unavailable);
          if (input) input.disabled = card.classList.contains("is-locked") || unavailable;
          const price = card.querySelector("[data-package-extra-price]");
          const compare = card.querySelector("[data-package-extra-compare]");
          const image = card.querySelector("[data-package-extra-image]");
          this.setText(price, this.money(this.extraDiscountPrice(variant, discount)));
          this.setText(compare, this.money(this.standalonePrice(variant)));
          const source = this.resizedImage(this.variantImage(variant), 144);
          if (image && source) image.src = source;
          const title = card.querySelector("[data-package-extra-title]");
          if (title) title.setAttribute("aria-label", `${title.textContent || "Extra harness"}: ${color} ${size}`.trim());
        });
        this.normalizeExtraSequence();
      }

      addonInput(addon) {
        return this.querySelector(`[data-package-addon="${addon}"] [data-package-input]`);
      }

      addonChecked(addon) {
        const input = this.addonInput(addon);
        return Boolean(input?.checked && !input.disabled);
      }

      packageName() {
        const includesBungee = this.addonChecked("bungee");
        const includesCollar = this.addonChecked("collar");
        const includesSafeBelt = this.addonChecked("safebelt");
        if (includesSafeBelt && includesCollar) return includesBungee ? "complete_set" : "harness_collar_safebelt";
        if (includesSafeBelt) return includesBungee ? "harness_bungee_safebelt" : "harness_safebelt";
        if (includesBungee && includesCollar) return "full_set";
        if (includesBungee) return "harness_bungee";
        if (includesCollar) return "harness_collar";
        return "harness_only";
      }

      money(cents) {
        if (typeof window.Elixir_FormatMoney === "function") return window.Elixir_FormatMoney(cents);
        if (window.Shopify?.formatMoney) return window.Shopify.formatMoney(cents, window.Shopify.money_format);
        return new Intl.NumberFormat(document.documentElement.lang || "en-US", {
          style: "currency",
          currency: window.Shopify?.currency?.active || "USD",
        }).format(Number(cents || 0) / 100);
      }

      comparePrice(variant) {
        if (!variant) return 0;
        const compare = Number(variant.compare_at_price ?? variant.compareAtPrice ?? 0);
        return compare > 0 ? compare : Number(variant.price || 0);
      }

      standalonePrice(variant) {
        return Number(variant?.price || 0);
      }

      bundlePrice(_addon, variant) {
        const standalone = this.standalonePrice(variant);
        if (standalone > 0 && this.addonDiscountPercentage > 0) {
          const discount = Math.floor((standalone * this.addonDiscountPercentage) / 100);
          return standalone - discount;
        }
        return standalone;
      }

      savingsPercentage(standalone, discounted) {
        if (standalone <= 0 || discounted >= standalone) return 0;
        return Math.round(((standalone - discounted) / standalone) * 100);
      }

      fitOfferLines() {
        this.querySelectorAll("[data-package-fit-line]").forEach(line => {
          if (!line.clientWidth) return;
          let size = Number(line.dataset.fitMax || 20);
          line.style.fontSize = `${size}px`;
          if (line.scrollWidth > line.clientWidth) {
            size *= line.clientWidth / line.scrollWidth;
            line.style.fontSize = `${Math.floor(size * 10) / 10}px`;
          }
        });
      }

      syncCollarControls(selection) {
        const colorIndex = this.optionIndex(this.collarOptions, this.colorOptionName, 0);
        const color = this.collarColorOverride || selection.collar?.options?.[colorIndex] || selection.color;
        this.querySelectorAll("[data-package-collar-color]").forEach(select => {
          Array.from(select.options).forEach(option => {
            option.disabled = !this.matchingVariant(this.collarVariants, this.collarOptions, option.value, selection.size, true);
          });
          select.value = color || "";
        });
      }

      updateOfferHeading(variant) {
        if (!this.offerHeading || !this.offerHeadingTemplate.includes("[discount]")) return;
        const price = this.standalonePrice(variant);
        const compare = this.comparePrice(variant);
        const discount = this.savingsPercentage(compare, price);
        this.offerHeading.textContent = this.offerHeadingTemplate.replaceAll("[discount]", String(discount));
      }

      variantImage(variant) {
        return variant?.featured_image?.src || variant?.featured_image?.url || variant?.featured_media?.preview_image?.src || "";
      }

      resizedImage(source, width = 144) {
        if (!source) return "";
        try {
          const url = new URL(source, window.location.origin);
          if (url.hostname.endsWith("cdn.shopify.com") || url.pathname.includes("/cdn/shop/")) {
            url.searchParams.set("width", String(width));
          }
          return url.href;
        } catch {
          return source;
        }
      }

      syncCardMedia(card, variants) {
        card.querySelectorAll("[data-package-product-image]").forEach((image) => {
          const variant = variants[image.dataset.packageProductImage];
          const originalSource = this.variantImage(variant);
          const source = this.resizedImage(originalSource, 144) || image.dataset.packageFallbackSrc;
          if (source && image.src !== source) {
            image.removeAttribute("srcset");
            image.src = source;
          }
          image.dataset.packageZoomSrc = this.resizedImage(originalSource, 720) || source;
        });
      }

      warmInfoImage(addon) {
        const card = (addon === "kit" ? this.productScope : this).querySelector(`[data-package-addon="${addon}"]`);
        const source = card?.querySelector("[data-package-product-image]")?.dataset.packageZoomSrc;
        if (!source) return null;
        if (this.infoImageCache.has(source)) return this.infoImageCache.get(source);
        const detailImage = new Image();
        detailImage.decoding = "async";
        const entry = { image: detailImage, ready: null };
        entry.ready = new Promise((resolve) => {
          detailImage.onload = () => resolve(true);
          detailImage.onerror = () => resolve(false);
        });
        this.infoImageCache.set(source, entry);
        detailImage.src = source;
        return entry;
      }

      scheduleInfoMedia() {
        window.clearTimeout(this.infoWarmTimer);
        if (!this.infoMediaInView || navigator.connection?.saveData) return;
        this.infoWarmTimer = window.setTimeout(() => {
          if (!this.isConnected) return;
          this.cards.forEach((card) => this.warmInfoImage(card.dataset.packageAddon));
        }, 350);
      }

      observeInfoMedia() {
        if (!this.infoDialog || !window.IntersectionObserver) return;
        this.infoMediaObserver?.disconnect();
        this.infoMediaObserver = new IntersectionObserver((entries) => {
          this.infoMediaInView = entries.some((entry) => entry.isIntersecting);
          this.scheduleInfoMedia();
        }, { rootMargin: "150px" });
        this.infoMediaObserver.observe(this);
      }

      updateCard(card, variant, color, size) {
        const input = card.querySelector("[data-package-input]");
        const status = card.querySelector("[data-package-status]");
        const price = card.querySelector("[data-package-addon-price]");
        const compare = card.querySelector("[data-package-addon-compare]");
        const savings = card.querySelector("[data-package-addon-savings]");
        const matches = card.querySelectorAll("[data-package-match]");
        const addon = card.dataset.packageAddon;
        const available = Boolean(variant?.available);
        const currentPrice = this.bundlePrice(addon, variant);
        const comparePrice = this.standalonePrice(variant);

        if (input && !available) input.checked = false;
        if (input) input.disabled = !available;
        card.classList.toggle("is-disabled", !available);
        card.classList.toggle("is-selected", input?.checked === true);
        if (price) price.textContent = this.money(currentPrice);
        if (compare) {
          compare.textContent = this.money(comparePrice);
          compare.hidden = comparePrice <= currentPrice;
        }
        if (savings) {
          const template = savings.dataset.savingsTemplate || "Save [discount]%";
          const discount = this.savingsPercentage(comparePrice, currentPrice);
          savings.textContent = template.replaceAll("[discount]", String(discount));
          savings.hidden = discount <= 0;
        }
        if (status) status.hidden = available;
        matches.forEach(match => { match.textContent = addon === "collar" && size ? `${color || ""} · ${size}` : color || ""; });
      }

      trackUpsell(action, addon, extra = {}) {
        if (!["safebelt","bungee","kit","collar"].includes(addon)) return;
        const selection = this.currentSelection();
        this.upsellStages ||= {};
        const stage = this.upsellStages[addon] ||= {};
        if (action === "viewed") stage.viewed = true;
        if (action === "details_opened") stage.details = true;
        if (action === "selected") stage.source = extra.source;
        const displayedSlot = (this.cards || []).findIndex(card => card.dataset.packageAddon === addon) + 1;
        const tracked = window.FourPawAnalytics?.publish?.("fourpaw:upsell_interaction", {
          action, accessory:addon, slot:displayedSlot || (addon === "safebelt" ? 2 : ["kit","collar"].includes(addon) ? 3 : 1), journey:this.upsellJourney,
          color:this.upsellColor(extra.color || (["safebelt","bungee","collar"].includes(addon) ? selection[addon]?.options?.[0] : selection.color)),
          source:extra.source, feature:extra.feature, seconds:extra.seconds,
        });
        const card = (addon === "kit" ? this.productScope : this).querySelector(`[data-package-addon="${addon}"]`);
        if (tracked && card) {
          card.dataset.fpLastTrackedAction = action;
          card.dataset.fpTrackedInteractions = String(Number(card.dataset.fpTrackedInteractions || 0) + 1);
        }
      }

      syncSafeBeltControls(selection) {
        const variant = selection?.safebelt;
        if (!variant) return;
        this.querySelectorAll("[data-package-safebelt-color]").forEach(select => { select.value = String(variant.id); });
        this.querySelectorAll("[data-package-info-add='safebelt']").forEach(add => {
          add.disabled = !variant.available;
          add.querySelector("[data-package-info-add-label]").textContent = selection.includesSafeBelt ? add.dataset.selectedLabel : add.dataset.addLabel;
          add.querySelector("[data-package-info-add-price]").textContent = this.money(this.bundlePrice("safebelt", variant));
        });
      }

      observeUpsellExposure() {
        if (!window.IntersectionObserver) return;
        this.upsellObserver?.disconnect();
        this.upsellObserver = new IntersectionObserver(entries => {
          entries.forEach(entry => {
            const addon = entry.target.dataset.packageAddon;
            if (!["safebelt","bungee","kit","collar"].includes(addon)) return;
            const prior = this.upsellExposureTimers.get(addon);
            if (!entry.isIntersecting || entry.intersectionRatio < .5) {
              window.clearTimeout(prior); this.upsellExposureTimers.delete(addon); return;
            }
            if (this.upsellExposed.has(addon) || prior) return;
            this.upsellExposureTimers.set(addon, window.setTimeout(() => {
              this.upsellExposureTimers.delete(addon);
              if (!this.isConnected || document.visibilityState === "hidden") return;
              this.upsellExposed.add(addon); this.trackUpsell("viewed", addon);
            }, 1000));
          });
        }, {threshold:[0,.5]});
        this.querySelectorAll("[data-package-card]").forEach(card => this.upsellObserver.observe(card));
        const kitCard = this.productScope.querySelector("[data-package-kit-offer]");
        if (kitCard) this.upsellObserver.observe(kitCard);
      }

      observePopupEnd() {
        this.popupEndObserver?.disconnect();
        const end = this.infoDialog?.querySelector("[data-package-popup-bottom]");
        const addon = this.infoDialog?.dataset.packageAddon;
        if (!window.IntersectionObserver || !end || !["safebelt","bungee","collar"].includes(this.infoDialog.dataset.packageAddon)) return;
        this.popupEndObserver = new IntersectionObserver(entries => {
          window.clearTimeout(this.popupEndTimer);
          if (entries.some(entry => entry.isIntersecting) && !this.popupEndTracked) this.popupEndTimer = window.setTimeout(() => {
            if (!this.infoDialog?.open || this.infoDialog.dataset.packageAddon !== addon || document.visibilityState === "hidden") return;
            this.popupEndTracked = true; this.trackUpsell("details_end_viewed", addon);
          }, 1000);
        }, {root:this.infoDialog.querySelector(".fourpaw-package__dialog-shell"),threshold:1});
        this.popupEndObserver.observe(end);
      }

      upsellColor(value) {
        const colors = {
          Black:["black","schwarz","noir","negro","nero"],Beige:["beige"],Red:["red","rot","rouge","rojo","rosso"],
          "Dark Blue":["dark blue","dunkelblau","bleu foncé","azul oscuro","blu scuro"],Pink:["pink","rosa","rose"],
          Orange:["orange","naranja","arancione"],Green:["green","grün","vert","verde"],Gray:["gray","grey","grau","gris","grigio"],
          "Sky Blue":["sky blue","hellblau","bleu ciel","azul cielo","azul celeste","azzurro"],Aqua:["aqua","türkis","turquoise","turquesa","acqua","turchese"]
        };
        return Object.entries(colors).find(([,values]) => values.includes(this.normalize(value)))?.[0] || value;
      }

      kitOffer(selection) {
        const kit = (this.kitVariants || []).find(v => this.upsellColor(v.options?.[0]) === this.upsellColor(selection.color) && this.normalize(v.options?.[1]) === this.normalize(selection.size));
        const belt = this.safebeltVariants.find(v => this.upsellColor(v.options?.[0]) === "Black");
        if (!kit || !selection.main || !selection.bungee || !selection.collar || !belt) return null;
        const separate = [selection.main,selection.bungee,selection.collar,belt].reduce((n,v) => n + this.standalonePrice(v),0);
        return {kit,separate,saving:separate - Number(kit.price),available:kit.available !== false};
      }

      syncKitOffer(selection) {
        const card = this.productScope.querySelector("[data-package-kit-offer]");
        if (!card) return;
        const offer = this.kitOffer(selection);
        card.hidden = !offer || offer.saving <= 0 || !offer.available;
        if (card.hidden) return;
        card.querySelector("[data-package-kit-price]").textContent = this.money(Number(offer.kit.price));
        card.querySelector("[data-package-kit-separate]").textContent = this.money(offer.separate);
        card.querySelector("[data-package-kit-saving]").textContent = this.money(offer.saving);
        card.querySelector("[data-package-kit-match]").textContent = `${selection.color} · ${selection.size}`;
        const image = card.querySelector("img");
        if (image && offer.kit.featured_image?.src) image.src = offer.kit.featured_image.src.replace(/width=\d+/,"width=216");
        const link = card.querySelector("[data-package-kit-link]");
        const url = new URL(link.dataset.kitUrl, window.location.origin);
        url.searchParams.set("variant",String(offer.kit.id));
        url.searchParams.set("fp_upsell_journey",this.upsellJourney);
        link.href = url.href;
      }

      upsellEvidence(role) {
        const stages = this.upsellStages || {};
        let experience = {};
        try {
          const arm = document.querySelector("[data-fp-pdp-arm]")?.dataset.fpPdpArm;
          if (["control", "challenger"].includes(arm) &&
              window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() === true) experience = {pdp_arm: arm};
        } catch (_error) {}
        return JSON.stringify({...experience,build:"upsell-v2",environment:window.Shopify?.theme?.role === "main" ? "live" : "preview",journey:this.upsellJourney,role,
          order:(this.cards || []).map(card => card.dataset.packageAddon).filter(k => ["bungee","safebelt","collar"].includes(k)),
          viewed:["bungee","safebelt","kit","collar"].filter(k => stages[k]?.viewed),details:["bungee","safebelt","collar"].filter(k => stages[k]?.details),
          sources:Object.fromEntries(["bungee","safebelt","collar"].filter(k => ["card","popup"].includes(stages[k]?.source)).map(k => [k,stages[k].source]))});
      }

      attachUpsellEvidence(data) {
        if (!data || !this.upsellJourney) return;
        const selection = this.currentSelection();
        for (const [name,value] of Array.from(data.entries())) {
          const match = name.match(/^items\[(\d+)\]\[id\]$/);
          if (!match) continue;
          const role = String(value) === String(selection.main?.id) ? "harness" : selection.includesBungee && String(value) === String(selection.bungee?.id) ? "bungee" : selection.includesSafeBelt && String(value) === String(selection.safebelt?.id) ? "safebelt" : selection.includesCollar && String(value) === String(selection.collar?.id) ? "collar" : null;
          if (role) data.set(`items[${match[1]}][properties][_FP upsell]`,this.upsellEvidence(role));
        }
      }

      replaceInfoTokens(value, color, size) {
        return String(value || "")
          .replaceAll("[color]", color || "")
          .replaceAll("[size]", size || "");
      }

      openInfo(addon, shouldOpen, opener = null, source = "details") {
        if (!this.infoDialog || !addon) return;
        const selection = this.currentSelection();
        const variant = selection[addon];
        const infoColor = ["safebelt","bungee","collar"].includes(addon) ? variant?.options?.[0] : selection.color;
        const card = (addon === "kit" ? this.productScope : this).querySelector(`[data-package-addon="${addon}"]`);
        const button = card?.querySelector("[data-package-info-button]");
        if (!card || !button || !variant) return;

        const image = card.querySelector("[data-package-product-image]");
        const dialogImage = this.infoDialog.querySelector("[data-package-info-image]");
        const title = this.infoDialog.querySelector("[data-package-info-title]");
        this.infoDialog.querySelector(".fourpaw-package__benefit-visual")?.setAttribute("aria-label", `${button.dataset.infoTitle || ""} · ${infoColor || ""}`);
        const match = this.infoDialog.querySelector("[data-package-info-match]");
        const copy = this.infoDialog.querySelector("[data-package-info-copy]");
        const standalone = this.infoDialog.querySelector("[data-package-info-standalone]");
        const bundle = this.infoDialog.querySelector("[data-package-info-bundle]");

        if (dialogImage) {
          const detailSource = image?.dataset.packageZoomSrc || "";
          const entry = this.warmInfoImage(addon);
          dialogImage.dataset.expectedSource = detailSource;
          dialogImage.src = entry?.image.complete && entry.image.naturalWidth > 0
            ? detailSource
            : image?.currentSrc || image?.src || "";
          entry?.ready.then((loaded) => {
            if (loaded && this.isConnected && dialogImage.dataset.expectedSource === detailSource) {
              dialogImage.src = detailSource;
            }
          });
          dialogImage.alt = this.replaceInfoTokens(button.dataset.infoImageAlt, ["safebelt","bungee","collar"].includes(addon) ? variant.options?.[0] : selection.color, selection.size);
        }
        this.infoDialog.querySelectorAll("[data-package-bungee-benefits]").forEach((element) => {
          element.toggleAttribute("hidden", addon !== "bungee");
        });
        this.infoDialog.querySelectorAll("[data-package-safebelt-benefits]").forEach(element => element.toggleAttribute("hidden", addon !== "safebelt"));
        this.infoDialog.querySelectorAll("[data-package-collar-benefits]").forEach(element => element.toggleAttribute("hidden", addon !== "collar"));
        this.querySelectorAll("[data-package-info-add='collar']").forEach(add => {
          add.disabled = !selection.collar?.available;
          add.querySelector("[data-package-info-add-label]").textContent = selection.includesCollar ? add.dataset.selectedLabel : add.dataset.addLabel;
          add.querySelector("[data-package-info-add-price]").textContent = this.money(this.bundlePrice("collar", selection.collar));
        });
        this.infoDialog.style.setProperty(
          "--fourpaw-package-dialog-media-bg",
          button.dataset.infoMediaBackground || "#ffffff",
        );
        if (title) title.textContent = button.dataset.infoTitle || "";
        if (match) match.textContent = card.querySelector("[data-package-match]")?.textContent || "";
        if (copy) {
          const value = this.replaceInfoTokens(button.dataset.infoCopy, infoColor, selection.size);
          const parts = value.match(/^(.+?[.!?])\s+(.+)$/s);
          const lead = document.createElement("strong");
          lead.textContent = parts ? parts[1] : value;
          const rest = document.createElement("span");
          rest.textContent = parts ? parts[2] : "";
          copy.replaceChildren(lead, rest);
        }
        if (standalone) standalone.textContent = this.money(this.standalonePrice(variant));
        if (bundle) bundle.textContent = this.money(this.bundlePrice(addon, variant));
        this.infoDialog.querySelectorAll("[data-package-info-benefit]").forEach((item) => {
          const value = this.replaceInfoTokens(
            button.getAttribute(`data-info-benefit-${item.dataset.packageInfoBenefit}`),
            infoColor,
            selection.size,
          );
          item.textContent = value;
          item.hidden = !value;
        });
        this.infoDialog.dataset.packageAddon = addon;
        const isBelt = addon === "safebelt";
        const offerCompare = this.standalonePrice(variant);
        const offerPrice = this.bundlePrice(addon, variant);
        const hasSaving = offerCompare > offerPrice;
        if (standalone) standalone.hidden = !hasSaving;
        const standaloneLabel = this.infoDialog.querySelector("[data-package-info-standalone-label]");
        if (standaloneLabel) {
          standaloneLabel.hidden = !hasSaving;
          standaloneLabel.textContent = isBelt ? button.dataset.infoCompareLabel : standaloneLabel.dataset.defaultLabel;
        }
        const saving = this.infoDialog.querySelector("[data-package-info-saving]");
        if (saving) {
          saving.textContent = `${saving.dataset.savingLabel} ${this.savingsPercentage(offerCompare, offerPrice)}%`;
          saving.hidden = !hasSaving;
        }
        const priceLabel = this.infoDialog.querySelector("[data-package-info-bundle-label]");
        if (priceLabel) priceLabel.textContent = isBelt ? button.dataset.infoPriceLabel : priceLabel.dataset.defaultLabel;
        const pricingNote = this.infoDialog.querySelector(".fourpaw-package__dialog-note");
        if (pricingNote) pricingNote.hidden = isBelt;
        this.syncSafeBeltControls(selection);
        this.querySelectorAll("[data-package-info-add='bungee']").forEach(add => {
          add.disabled = !variant.available || addon !== "bungee";
          add.querySelector("[data-package-info-add-label]").textContent = selection.includesBungee ? add.dataset.selectedLabel : add.dataset.addLabel;
          add.querySelector("[data-package-info-add-price]").textContent = this.money(this.bundlePrice("bungee", selection.bungee));
        });
        if (shouldOpen && !this.infoDialog.open) {
          this.infoReturnFocus = opener || button;
          this.infoOpenedAt = Date.now();
          this.popupEndTracked = false;
          this.trackUpsell("details_opened", addon, {source});
          this.infoDialog.querySelector(".fourpaw-package__dialog-shell")?.scrollTo(0, 0);
          this.infoDialog.showModal();
          this.observePopupEnd();
        }
      }

      currentSelection() {
        const main = this.currentMainVariant();
        if (!main) return { available: false, main: null };
        const colorIndex = this.optionIndex(this.mainOptions, this.colorOptionName, 0);
        const sizeIndex = this.optionIndex(this.mainOptions, this.sizeOptionName, 1);
        const color = main.options?.[colorIndex];
        const size = main.options?.[sizeIndex];
        if (this.bungeeOverrideMainColor && this.bungeeOverrideMainColor !== this.normalize(color)) {
          this.bungeeVariantId = 0;
          this.bungeeOverrideMainColor = "";
        }
        const bungee = this.bungeeVariants.find(v => this.bungeeVariantId && String(v.id) === String(this.bungeeVariantId)) || this.matchingVariant(this.bungeeVariants, this.bungeeOptions, color, size, false);
        if (this.collarOverrideMainColor && this.collarOverrideMainColor !== this.normalize(color)) {
          this.collarColorOverride = "";
          this.collarOverrideMainColor = "";
        }
        const collar = this.matchingVariant(this.collarVariants, this.collarOptions, this.collarColorOverride || color, size, true);
        const includesBungee = this.addonChecked("bungee");
        const includesCollar = this.addonChecked("collar");
        const safebelt = this.safebeltVariants.find(v => String(v.id) === String(this.safebeltVariantId));
        const includesSafeBelt = this.addonChecked("safebelt");
        const extras = this.extraSelections();
        const variants = [main];
        if (includesBungee) variants.push(bungee);
        if (includesCollar) variants.push(collar);
        if (includesSafeBelt) variants.push(safebelt);
        extras.filter((extra) => extra.selected).forEach((extra) => variants.push(extra.variant));
        let price = Number(main.price || 0);
        if (includesBungee) price += this.bundlePrice("bungee", bungee);
        if (includesCollar) price += this.bundlePrice("collar", collar);
        if (includesSafeBelt) price += this.bundlePrice("safebelt", safebelt);
        extras.filter((extra) => extra.selected).forEach((extra) => {
          price += extra.price;
        });
        return {
          main,
          bungee,
          collar,
          safebelt,
          includesSafeBelt,
          color,
          size,
          includesBungee,
          includesCollar,
          extras,
          available: this.giftAvailable && variants.every((variant) => Boolean(variant && variant.available !== false)),
          price,
        };
      }

      hiddenInput(name, value, role) {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = name;
        input.value = String(value);
        input.dataset.fourpawPackageLine = role;
        return input;
      }

      nextItemIndex() {
        let max = -1;
        this.form?.querySelectorAll('input[name^="items["]').forEach((input) => {
          if (input.dataset.fourpawPackageLine) return;
          const match = input.name.match(/^items\[(\d+)\]/);
          if (match) max = Math.max(max, Number(match[1]));
        });
        return max + 1;
      }

      addLine(index, variantId, role) {
        if (!this.form || !variantId) return index;
        this.form.append(
          this.hiddenInput(`items[${index}][id]`, variantId, role),
          this.hiddenInput(`items[${index}][quantity]`, 1, role),
          this.hiddenInput(`items[${index}][properties][_Package ID]`, this.packageGroup, role),
        );
        return index + 1;
      }

      addLineProperty(index, propertyName, value, role) {
        if (!this.form || !propertyName || value === undefined || value === null || value === "") return;
        this.form.append(
          this.hiddenInput(`items[${index}][properties][${propertyName}]`, value, role),
        );
      }

      renewKachingContractId() {
        if (!this.form || !this.kachingBridgeEnabled) return "";
        const fields = Array.from(
          this.form.querySelectorAll('input[name="properties[__kaching_bundles]"], input[name$="[properties][__kaching_bundles]"]'),
        );
        const contracts = fields.map((field) => {
          try {
            return { field, contract: JSON.parse(field.value || "{}") };
          } catch {
            return { field, contract: null };
          }
        });
        const mainContract = contracts.find(({ contract }) => contract?.main)?.contract;
        if (!mainContract?.id) return "";
        const nextId = `FP-${this.newGroup()}`;
        contracts.forEach(({ field, contract }) => {
          if (!contract || String(contract.id || "") !== String(mainContract.id)) return;
          field.value = JSON.stringify({ ...contract, id: nextId });
          field.setAttribute("value", field.value);
        });
        return nextId;
      }

      formLinesSignature(selection) {
        const personalization = Array.from(
          (this.productScope || this.form)?.querySelectorAll?.("[data-fourpaw-personalization-role]") || [],
        ).map((wrapper) => {
          const field = wrapper.matches("input, textarea") ? wrapper : wrapper.querySelector("input, textarea");
          return [wrapper.dataset.fourpawPersonalizationRole || "", String(field?.value || "").trim()];
        });
        const contract = this.form?.querySelector('input[name="properties[__kaching_bundles]"]')?.value || "";
        return JSON.stringify({
          packageGroup: this.packageGroup,
          contract,
          main: selection.main?.id || 0,
          bungee: selection.includesBungee ? selection.bungee?.id || 0 : 0,
          collar: selection.includesCollar ? selection.collar?.id || 0 : 0,
          safebelt: selection.includesSafeBelt ? selection.safebelt?.id || 0 : 0,
          extras: (selection.extras || []).filter((extra) => extra.selected).map((extra) => extra.variant?.id || 0),
          giftMode: this.giftMode,
          personalization,
          kachingBridgeActive: this.kachingBridgeActive,
          kachingSlots: this.kachingSlotMap(),
        });
      }

      copyMainProperties(index, includePersonalization = true) {
        if (!this.form) return;
        Array.from(this.form.elements).forEach((control) => {
          if (
            !control.name?.startsWith("properties[") ||
            control.dataset.fourpawPackageLine ||
            control.disabled ||
            ((control.type === "checkbox" || control.type === "radio") && !control.checked)
          ) {
            return;
          }
          const personalizationRole =
            control.closest?.("[data-fourpaw-personalization-role]")?.dataset.fourpawPersonalizationRole || "";
          if (!includePersonalization && ["dog_name", "phone"].includes(personalizationRole)) return;
          const propertyName = control.name.match(/^properties\[(.+)\]$/)?.[1];
          if (!propertyName || control.value === "") return;
          this.form.append(
            this.hiddenInput(`items[${index}][properties][${propertyName}]`, control.value, "main-personalization"),
          );
        });
      }

      copyGiftPersonalization(index) {
        if (!this.form) return;
        const formControls = Array.from(this.form.elements);
        ["dog_name", "phone"].forEach((role) => {
          const control = formControls.find(
            (field) =>
              field.name?.startsWith("properties[") &&
              field.closest?.("[data-fourpaw-personalization-role]")?.dataset.fourpawPersonalizationRole === role,
          );
          const propertyName = control?.name?.match(/^properties\[(.+)\]$/)?.[1];
          const value = String(control?.value || "").trim();
          if (propertyName && value) {
            this.addLineProperty(index, propertyName, value, "gift-personalization");
          }
        });
      }

      suspendRootFields() {
        if (!this.form) return;
        this.releaseRootFields();
        this.suspendedRootFields = Array.from(this.form.elements).filter(
          (control) =>
            !control.disabled &&
            !control.dataset.fourpawPackageLine &&
            (control.name === "id" || control.name === "quantity" || control.name?.startsWith("properties[")),
        );
        this.suspendedRootFields.forEach((control) => {
          control.disabled = true;
        });
      }

      releaseRootFields() {
        this.suspendedRootFields?.forEach((control) => {
          control.disabled = false;
        });
        this.suspendedRootFields = [];
      }

      harnessReferenceCode() {
        return `SW-${String(this.packageGroup || "").slice(-6).toUpperCase()}`;
      }

      giftHarnessReference(selection) {
        const variantDetails = [selection?.color, selection?.size].filter(Boolean).join(" / ");
        const reference = this.harnessReferenceCode();
        return variantDetails ? `${reference} · ${variantDetails}` : reference;
      }

      tagKachingAddonLines() {
        if (!this.form) return;
        this.form.querySelectorAll('input[name$="[properties][__kaching_bundles]"]').forEach((contractField) => {
          let contract;
          try {
            contract = JSON.parse(contractField.value || "{}");
          } catch {
            return;
          }
          if (!contract?.upsell || contract.main) return;
          const match = contractField.name.match(/^items\[(\d+)\]/);
          if (!match) return;
          const index = Number(match[1]);
          this.addLineProperty(index, "_Package ID", this.packageGroup, "kaching-package-group");
          this.addLineProperty(index, "_Harness reference", this.harnessReferenceCode(), "kaching-harness-reference");
        });
      }

      finalizeKachingFormData(formData) {
        if (!formData || !this.form) return;
        const entries = Array.from(formData.entries());
        const contractRows = [];
        const itemIds = new Map();
        const itemIndexes = new Set();
        for (const [name, value] of entries) {
          const itemMatch = name.match(/^items\[(\d+)\]/);
          if (itemMatch) itemIndexes.add(Number(itemMatch[1]));
          const idMatch = name.match(/^items\[(\d+)\]\[id\]$/);
          if (idMatch) itemIds.set(Number(idMatch[1]), String(value));
          const match = name.match(/^items\[(\d+)\]\[properties\]\[__kaching_bundles\]$/);
          if (!match) continue;
          try {
            contractRows.push({ index: Number(match[1]), name, contract: JSON.parse(String(value || "{}")) });
          } catch {
            return;
          }
        }

        const mainRow = contractRows.find((row) => row.contract?.main);
        const main = mainRow?.contract;
        const slots = this.kachingSlotMap();
        if (!main || !slots) return;
        const base = {
          deal: String(main.deal || ""),
          pp: Number(main.pp || 0),
          v: String(main.v || ""),
          id: String(main.id || ""),
          bid: String(main.bid || ""),
        };
        if (!base.deal || !base.pp || !base.v || !base.id || !base.bid) return;

        const accessorySlots = new Set([...(slots.b || []), ...(slots.c || [])].map(String));
        const selection = this.currentSelection?.();
        const desiredAccessories = [
          selection?.includesBungee && selection.bungee?.id
            ? { slot: String(slots.b?.[0] || ""), variantId: String(selection.bungee.id) }
            : null,
          selection?.includesCollar && selection.collar?.id
            ? { slot: String(slots.c?.[0] || ""), variantId: String(selection.collar.id) }
            : null,
        ].filter((item) => item?.slot);
        const desiredBySlot = new Map(desiredAccessories.map((item) => [item.slot, item]));
        const claimedSlots = new Set();
        const claimedIndexes = new Set();
        const removeItem = (index) => {
          const prefix = `items[${index}]`;
          Array.from(formData.keys()).forEach((name) => {
            if (name.startsWith(prefix)) formData.delete(name);
          });
          itemIds.delete(index);
          itemIndexes.delete(index);
        };
        const finalizeAccessory = (index, desired) => {
          formData.set(`items[${index}][id]`, desired.variantId);
          formData.set(`items[${index}][quantity]`, "1");
          formData.set(
            `items[${index}][properties][__kaching_bundles]`,
            JSON.stringify({ ...base, upsell: desired.slot }),
          );
          formData.set(`items[${index}][properties][_Package ID]`, this.packageGroup);
          formData.set(`items[${index}][properties][_Harness reference]`, this.harnessReferenceCode());
          claimedSlots.add(desired.slot);
          claimedIndexes.add(index);
          itemIds.set(index, desired.variantId);
          itemIndexes.add(index);
        };

        contractRows.forEach((row) => {
          if (row.contract?.main) {
            formData.set(row.name, JSON.stringify({ ...base, main: true }));
            return;
          }
          const upsell = String(row.contract?.upsell || "");
          if (!accessorySlots.has(upsell)) return;
          const desired = desiredBySlot.get(upsell);
          if (!desired || claimedSlots.has(upsell)) {
            removeItem(row.index);
            return;
          }
          finalizeAccessory(row.index, desired);
        });

        let nextIndex = itemIndexes.size ? Math.max(...itemIndexes) + 1 : 0;
        desiredAccessories.forEach((desired) => {
          if (claimedSlots.has(desired.slot)) return;
          const fallback = Array.from(itemIds.entries()).find(([index, id]) => (
            index !== mainRow.index &&
            !claimedIndexes.has(index) &&
            id === desired.variantId
          ));
          const index = fallback?.[0] ?? nextIndex++;
          finalizeAccessory(index, desired);
        });
      }

      syncFormLines(selection) {
        if (!this.form || !selection.main) return;
        const signature = this.formLinesSignature(selection);
        if (
          signature === this.lastFormLinesSignature &&
          this.form.querySelector("[data-fourpaw-package-line]")
        ) {
          return;
        }
        this.form.querySelectorAll("[data-fourpaw-package-line]").forEach((input) => input.remove());
        let index = this.nextItemIndex();
        const mainIndex = index;
        index = this.addLine(index, selection.main.id, "SafeWalk harness");
        // Private on the customer side, but retained in Shopify order details so
        // fulfillment can pair each Gift Pack with the correct harness line.
        this.addLineProperty(mainIndex, "_Harness reference", this.harnessReferenceCode(), "harness-reference");
        const kachingSlots = this.kachingSlotMap();
        if (kachingSlots) {
          this.addLineProperty(mainIndex, "_FPK", JSON.stringify(kachingSlots), "kaching-slot-map");
        }
        this.copyMainProperties(mainIndex, true);
        if (selection.includesBungee && !this.kachingBridgeActive) {
          index = this.addLine(index, selection.bungee?.id, "matching bungee leash");
        }
        if (selection.includesCollar && !this.kachingBridgeActive) {
          index = this.addLine(index, selection.collar?.id, "matching padded collar");
        }
        if (selection.includesSafeBelt) {
          const beltIndex = index;
          index = this.addLine(index, selection.safebelt?.id, "SafeBelt car seat belt");
          this.addLineProperty(beltIndex, "_Harness reference", this.harnessReferenceCode(), "belt-harness-reference");
        }
        if (this.giftAvailable && this.giftMode === "three_products") {
          const tagsIndex = index;
          index = this.addLine(index, this.giftTagsVariantId, "free SafeWalk gift tags");
          this.addLineProperty(tagsIndex, "_For harness", this.giftHarnessReference(selection), "gift-harness-reference");

          index = this.addLine(index, this.giftDispenserVariantId, "free SafeWalk gift dispenser");
          this.addLine(index, this.giftRollVariantId, "free SafeWalk gift roll");
        } else if (this.giftAvailable && this.giftVariantId) {
          const giftPackIndex = index;
          this.addLine(giftPackIndex, this.giftVariantId, "free SafeWalk gift pack");
          this.addLineProperty(giftPackIndex, "_For harness", this.giftHarnessReference(selection), "gift-harness-reference");

        }
        this.tagKachingAddonLines();
        this.lastFormLinesSignature = signature;
      }

      syncButton(available, unavailableOverride = "", selection = null) {
        const button = this.form?.querySelector('[type="submit"]');
        const text = button?.querySelector(".button-text");
        if (!button || !text) return;
        const label = this.dataset.buttonLabel || "Add to cart";
        const unavailableLabel = unavailableOverride || this.dataset.buttonUnavailableLabel || "Selected package unavailable";
        const price = Number(selection?.price || 0);
        const showPrice = button.dataset.showPrice === "true";
        button.dataset.persistentText = label;
        button.dataset.textOverride = "true";
        button.classList.toggle("fourpaw-package-unavailable", !available);
        button.disabled = !available;
        button.setAttribute("aria-disabled", available ? "false" : "true");
        text.textContent = !available
          ? `${label} · ${unavailableLabel}`
          : showPrice && price > 0
            ? `${label} · ${this.money(price)}`
            : label;
      }

      syncSticky(selection, available = selection.available) {
        if (!this.sticky) return;
        const titleNode = this.sticky.querySelector("[data-package-sticky-title]");
        const priceNode = this.sticky.querySelector("[data-package-sticky-price]");
        if (titleNode) {
          const details = [];
          if (selection.size) details.push(`${this.sizeOptionLabel} ${selection.size}`);
          if (selection.includesBungee) details.push(`+ ${this.bungeeTitle}`);
          if (selection.includesCollar) details.push(`+ ${this.collarTitle}`);
          if (selection.includesSafeBelt) details.push(`+ ${this.dataset.safebeltTitle || "SafeBelt"}`);
          titleNode.textContent = [this.harnessTitle, ...details].join(" · ");
        }
        if (priceNode) priceNode.textContent = this.money(selection.price);
        if (this.stickyButton) this.stickyButton.disabled = !available;
      }

      observePurchaseButton() {
        const button = this.form?.querySelector('[type="submit"]');
        if (!button || !this.sticky) return;
        this.purchaseObserver?.disconnect();
        this.purchaseButton = button;
        if (!this.onStickyPositionChange) {
          this.onStickyPositionChange = () => {
            if (this.stickyPositionFrame) return;
            this.stickyPositionFrame = window.requestAnimationFrame(() => {
              this.stickyPositionFrame = null;
              this.syncStickyVisibility();
            });
          };
        }
        if (!this.onStickyVisibilityChange) {
          this.onStickyVisibilityChange = () => {
            if (this.stickyPositionFrame) window.cancelAnimationFrame(this.stickyPositionFrame);
            this.stickyPositionFrame = null;
            this.onStickyPositionChange();
          };
        }
        window.removeEventListener("scroll", this.onStickyPositionChange);
        window.removeEventListener("resize", this.onStickyPositionChange);
        document.removeEventListener("visibilitychange", this.onStickyVisibilityChange);
        window.addEventListener("scroll", this.onStickyPositionChange, { passive: true });
        window.addEventListener("resize", this.onStickyPositionChange);
        document.addEventListener("visibilitychange", this.onStickyVisibilityChange);
        this.syncStickyVisibility();
      }

      syncStickyVisibility() {
        if (!this.purchaseButton || !this.sticky) return;
        const footer = document.querySelector("footer");
        const footerIsVisible = footer && footer.getBoundingClientRect().top < window.innerHeight;
        const buttonRect = this.purchaseButton.getBoundingClientRect();
        const buttonIsVisible = buttonRect.top < window.innerHeight && buttonRect.bottom > 0;
        const earlyScrollThreshold = Math.min(240, window.innerHeight * 0.25);
        const visible = window.scrollY >= earlyScrollThreshold && !buttonIsVisible && !footerIsVisible;
        this.sticky.classList.toggle("is-visible", visible);
        this.sticky.setAttribute("aria-hidden", visible ? "false" : "true");
        this.sticky.inert = !visible;
      }

      update() {
        this.bindForm();
        this.reconcileMainVariant();
        const initialSelection = this.currentSelection();
        if (!initialSelection.main) return;
        const variants = {
          main: initialSelection.main,
          bungee: initialSelection.bungee,
          collar: initialSelection.collar,
          safebelt: initialSelection.safebelt,
        };
        this.updateOfferHeading(initialSelection.main);
        this.fitOfferLines();
        this.syncCollarControls(initialSelection);
        this.cards.forEach((card) => {
          const variant = variants[card.dataset.packageAddon];
          this.updateCard(card, variant, ["safebelt","bungee","collar"].includes(card.dataset.packageAddon) ? variant?.options?.[0] || this.collarColorOverride : initialSelection.color, initialSelection.size);
          this.syncCardMedia(card, variants);
        });
        this.querySelectorAll("[data-package-bungee-color]").forEach(select => {select.value = String(initialSelection.bungee?.id || "");});
        this.syncSafeBeltControls(initialSelection);
        this.syncKitOffer(initialSelection);
        this.scheduleInfoMedia();
        this.selectedPackage = this.packageName();
        let selection = this.currentSelection();
        this.syncKaching(selection);
        this.reconcileMainVariant();
        this.updateExtraCards();
        selection = this.currentSelection();
        this.syncFormLines(selection);
        const available = this.purchaseAvailable(selection);
        this.syncSticky(selection, available);
        if (this.infoDialog?.open && this.infoDialog.dataset.packageAddon) {
          this.openInfo(this.infoDialog.dataset.packageAddon, false);
        }
        window.requestAnimationFrame(() =>
          this.syncButton(available, available ? "" : this.discountLoadingLabel, selection),
        );
      }
    },
  );
}



